import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export const EASE = [0.16, 1, 0.3, 1];

export function usePageMeta(title, description) {
  useEffect(() => {
    document.title = title;
    const m = document.querySelector('meta[name="description"]');
    if (m && description) m.setAttribute("content", description);
  }, [title, description]);
}

export const FadeIn = ({ children, delay = 0, y = 32, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, delay, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

export const Eyebrow = ({ children, tone = "bone", className = "" }) => (
  <p
    className={`flex items-center gap-3 font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] ${
      tone === "ink" ? "text-ink/70" : "text-ash"
    } ${className}`}
  >
    <span className="inline-block h-1.5 w-1.5 bg-lime" />
    {children}
  </p>
);

export const MaskedLines = ({ lines, className = "", lineClassName = "", delay = 0 }) => (
  <div className={className}>
    {lines.map((l, i) => (
      <span key={i} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
        <motion.span
          className={`block ${lineClassName}`}
          initial={{ y: "115%" }}
          animate={{ y: 0 }}
          transition={{ duration: 1, delay: delay + i * 0.13, ease: EASE }}
        >
          {l}
        </motion.span>
      </span>
    ))}
  </div>
);

const BUTTON_STYLES = {
  lime: "bg-lime text-white hover:bg-limed",
  ink: "bg-ink text-white hover:bg-coal",
  white: "bg-white text-limed hover:bg-bone",
  outlineLight: "border border-bone/30 text-bone hover:border-lime hover:text-lime",
  outlineInk: "border border-ink/30 text-ink hover:border-ink hover:bg-ink hover:text-bone",
  outlineCobalt: "border border-white/40 text-white hover:bg-white hover:text-limed",
};

export const ButtonLink = ({ to, children, variant = "lime", testid, className = "" }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`group inline-flex items-center gap-3 px-7 py-4 font-mono text-[11px] sm:text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${BUTTON_STYLES[variant]} ${className}`}
  >
    {children}
    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
  </Link>
);

export const PageHero = ({ eyebrow, lines, copy, children, testid }) => (
  <section
    data-testid={testid}
    className="relative overflow-hidden bg-ink px-6 pb-20 pt-36 text-bone sm:px-10 sm:pt-44 sm:pb-28 lg:px-16"
  >
    <div className="mx-auto max-w-[1440px]">
      <FadeIn>
        <Eyebrow>{eyebrow}</Eyebrow>
      </FadeIn>
      <h1 className="mt-8 font-display text-[13vw] font-black uppercase leading-[0.92] tracking-tight sm:text-[9vw] lg:text-[6.5vw]">
        <MaskedLines lines={lines} delay={0.1} />
      </h1>
      {copy && (
        <FadeIn delay={0.5} className="mt-10 max-w-xl">
          <p className="text-base leading-relaxed text-ash sm:text-lg">{copy}</p>
        </FadeIn>
      )}
      {children}
    </div>
  </section>
);
